<template>
  <div class="relative inline-block">
    <div ref="triggerRef" @click.stop="togglePicker">
      <slot></slot>
    </div>
    <Teleport to="body">
    <div
      v-if="showPicker"
      ref="pickerRef"
        class="fixed z-[9999]"
      :style="pickerPosition"
        @mouseenter="handlePickerMouseEnter"
        @mouseleave="handlePickerMouseLeave"
        @click.stop
    >
      <EmojiPickerComponent :native="true" @select="handleEmojiSelect" />
    </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, getCurrentInstance } from "vue";
import EmojiPickerComponent from "vue3-emoji-picker";
import "vue3-emoji-picker/css";

const props = defineProps({
  modelValue: { type: String, default: "" },
  placement: { type: String, default: "bottom" },
});

const emit = defineEmits(["update:modelValue", "picker-open", "picker-close", "picker-mouseenter", "picker-mouseleave"]);

const showPicker = ref(false);
const triggerRef = ref(null);
const pickerRef = ref(null);
const pickerPosition = ref({});

const instance = getCurrentInstance();
const pickerId = instance.uid;

const PICKER_EVENT = 'emoji-picker-toggle';

const togglePicker = () => {
  if (!showPicker.value) {
    window.dispatchEvent(new CustomEvent(PICKER_EVENT, { detail: { pickerId } }));
  }
  
  showPicker.value = !showPicker.value;
  if (showPicker.value) {
    emit("picker-open");
    nextTick(() => {
      calculatePosition();
      setTimeout(() => {
        calculatePosition();
      }, 50);
    });
  } else {
    emit("picker-close");
  }
};

const handleOtherPickerOpened = (event) => {
  if (event.detail.pickerId !== pickerId && showPicker.value) {
    showPicker.value = false;
    emit("picker-close");
  }
};

const calculatePosition = () => {
  if (!triggerRef.value) return;

  const rect = triggerRef.value.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  
  let pickerWidth = 352;
  let pickerHeight = 435;
  
  if (pickerRef.value) {
    const pickerRect = pickerRef.value.getBoundingClientRect();
    if (pickerRect.width > 0) pickerWidth = pickerRect.width;
    if (pickerRect.height > 0) pickerHeight = pickerRect.height;
  }
  
  const margin = 8;
  let top, left;

  if (props.placement === 'bottom-end') {
    top = rect.bottom + 5;
    left = rect.right - pickerWidth;
    
    if (left < margin) {
      left = margin;
    }
    
    if (rect.left + pickerWidth <= viewportWidth - margin) {
      left = rect.left;
    } else {
      left = viewportWidth - pickerWidth - margin;
    }
    
    if (top + pickerHeight > viewportHeight - margin) {
      top = rect.top - pickerHeight - 5;
      if (top < margin) {
        top = margin;
      }
    }
    
    pickerPosition.value = { 
      top: `${top}px`,
      left: `${left}px`
    };
  } else if (props.placement === 'bottom') {
    left = rect.left;
    top = rect.bottom + 5;
    
    if (left + pickerWidth > viewportWidth - margin) {
      left = viewportWidth - pickerWidth - margin;
    }
    if (left < margin) {
      left = margin;
    }
    
    if (top + pickerHeight > viewportHeight - margin) {
      top = rect.top - pickerHeight - 5;
      if (top < margin) {
        top = margin;
      }
    }
    
    pickerPosition.value = { 
      top: `${top}px`,
      left: `${left}px`
    };
  } else if (props.placement === 'top') {
    left = rect.left;
    top = rect.top - pickerHeight - 5;
    
    if (left + pickerWidth > viewportWidth - margin) {
      left = viewportWidth - pickerWidth - margin;
    }
    if (left < margin) {
      left = margin;
    }
    
    if (top < margin) {
      top = rect.bottom + 5;
      if (top + pickerHeight > viewportHeight - margin) {
        top = margin;
      }
    }
    
    pickerPosition.value = { 
      top: `${top}px`,
      left: `${left}px`
    };
  } else if (props.placement === 'right') {
    left = rect.right + 5;
    top = rect.top;
    
    if (left + pickerWidth > viewportWidth - margin) {
      left = rect.left - pickerWidth - 5;
      if (left < margin) {
        left = margin;
      }
    }
    
    if (top + pickerHeight > viewportHeight - margin) {
      top = viewportHeight - pickerHeight - margin;
    }
    if (top < margin) {
      top = margin;
    }

    pickerPosition.value = { 
      top: `${top}px`,
      left: `${left}px`
    };
  } else if (props.placement === 'left') {
    left = rect.left - pickerWidth - 5;
    top = rect.top;
    
    if (left < margin) {
      left = rect.right + 5;
      if (left + pickerWidth > viewportWidth - margin) {
        left = margin;
      }
    }
    
    if (top + pickerHeight > viewportHeight - margin) {
      top = viewportHeight - pickerHeight - margin;
    }
    if (top < margin) {
      top = margin;
    }
    
    pickerPosition.value = { 
      top: `${top}px`,
      left: `${left}px`
    };
  }
};

const handleEmojiSelect = (emoji) => {
  const emojiChar = emoji.i || emoji.native || emoji;
  emit("update:modelValue", emojiChar);
  showPicker.value = false;
  emit("picker-close");
};

const handlePickerMouseEnter = () => {
  emit('picker-mouseenter');
};

const handlePickerMouseLeave = () => {
  emit('picker-mouseleave');
};

const handleClickOutside = (event) => {
  if (
    pickerRef.value &&
    !pickerRef.value.contains(event.target) &&
    !triggerRef.value.contains(event.target)
  ) {
    showPicker.value = false;
    emit("picker-close");
  }
};

const handleScrollOrResize = () => {
  if (showPicker.value) {
    calculatePosition();
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  window.addEventListener("scroll", handleScrollOrResize, true);
  window.addEventListener("resize", handleScrollOrResize);
  window.addEventListener(PICKER_EVENT, handleOtherPickerOpened);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("scroll", handleScrollOrResize, true);
  window.removeEventListener("resize", handleScrollOrResize);
  window.removeEventListener(PICKER_EVENT, handleOtherPickerOpened);
});

defineExpose({
  showPicker,
  togglePicker
});
</script>
